import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { dictionaryFor, type Dictionary } from './dictionary'
import { DEFAULT_LOCALE, localeDef, type LocaleDef } from './locales'

interface I18nValue {
  locale: string
  def: LocaleDef
  t: Dictionary
}

const I18nContext = createContext<I18nValue>({
  locale: DEFAULT_LOCALE,
  def: localeDef(DEFAULT_LOCALE),
  t: dictionaryFor(DEFAULT_LOCALE),
})

export function I18nProvider({ locale, children }: { locale: string; children: ReactNode }) {
  const value = useMemo(
    () => ({ locale, def: localeDef(locale), t: dictionaryFor(locale) }),
    [locale],
  )
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

/**
 * The current locale's content, already merged over English.
 *
 * There is no `t('some.key')` string lookup, on purpose: `t.pages.home.hero.headline`
 * is checked by the compiler, renamed by the editor, and found by grep. A key
 * held in a string is none of those, and the failure mode — a typo rendering the
 * key itself into the page — is one this site cannot afford.
 */
export function useI18n(): I18nValue {
  return useContext(I18nContext)
}
